import {
  AlignmentType,
  convertInchesToTwip,
  Document,
  Footer,
  ImageRun,
  NumberFormat,
  Packer,
  PageNumber,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
} from 'docx';

import { QUESTION_TYPES, VW_DISPLAY_ORDERS } from '@api/instant_survey';

import {
  getMaxDiffQuestionInfo,
  getNumericTypeText,
  getQuestionCost,
  getQuestionDisplayIndexById,
} from '../../../pages/CreateInstantSurvey/utilities/helpers';
import {
  A_CHAR_CODE,
  BASE_TEXT_CONFIG,
  BLOCK_SPACING,
  BOLD_TEXT_CONFIG,
  BORDERLESS_TABLE_CONFIG,
  BULLET_SYMBOL,
  BULLETED_LIST_CONFIG,
  CHECKBOX_SYMBOL,
  DASH_SYMBOL,
  DEFAULT_QUESTION_RENDER_CONTEXT,
  DEFAULT_SPACING,
  EM_SPACE,
  FILLED_CIRCLE_SYMBOL,
  HEADER_SPACING,
  INDENT_LG,
  MAX_IMAGE_WIDTH,
  NESTED_QUESTION_RENDER_CONTEXT,
  NUMERATOR_FULL_LOGO_URL,
  QUESTION_SPACING,
  RADIO_SYMBOL,
  SMALL_TEXT_SIZE,
} from './constants';

const MONADIC_SECTION_TITLE = 'Monadic Section';

const getImageUrl = (image) => image?.cdnUrl || image?.previewUrl || image?.url;

const getActiveDocxItems = (items = []) => (items || []).filter((item) => !item?._delete);

const getActiveDocxQuestion = (question = {}) => ({
  ...question,
  answers: getActiveDocxItems(question.answers),
  concepts: getActiveDocxItems(question.concepts),
  subQuestions: getActiveDocxItems(question.subQuestions).map(getActiveDocxQuestion),
});

export const getActiveDocxQuestions = (questions = []) =>
  getActiveDocxItems(questions).map(getActiveDocxQuestion);

const hasImageUrl = (item) => Boolean(getImageUrl(item?.image));

const getConceptImageKey = (question, concept, conceptIndex) =>
  `${question.id ?? question.questionClientId ?? 'question'}-${concept.id ?? conceptIndex}`;

const getQuestionImageFetchItems = (questions = []) =>
  getActiveDocxItems(questions)
    .flatMap((question) => [question, ...getQuestionImageFetchItems(question.subQuestions)])
    .filter(hasImageUrl);

const getAnswerImageFetchItems = (questions = []) =>
  getActiveDocxItems(questions)
    .flatMap((question) => [
      ...getActiveDocxItems(question.answers),
      ...getAnswerImageFetchItems(question.subQuestions),
    ])
    .filter(hasImageUrl);

const getConceptImageFetchItems = (questions = []) =>
  getActiveDocxItems(questions)
    .flatMap((question) => [
      ...(question.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
        ? getActiveDocxItems(question.concepts).map((concept, conceptIndex) => ({
            id: getConceptImageKey(question, concept, conceptIndex),
            image: concept.image,
          }))
        : []),
      ...getConceptImageFetchItems(question.subQuestions),
    ])
    .filter(hasImageUrl);

export const getDocxImageFetchItems = (questions = []) => ({
  answerItems: getAnswerImageFetchItems(questions),
  conceptItems: getConceptImageFetchItems(questions),
  questionItems: getQuestionImageFetchItems(questions),
});

export const getSequentialMonadicSectionDetails = (question = {}) => {
  const activeQuestion = getActiveDocxQuestion(question);
  const activeConcepts = activeQuestion.concepts;
  const activeSubQuestions = activeQuestion.subQuestions;

  return {
    activeConcepts,
    activeSubQuestions,
    conceptCount: activeConcepts.length,
    questionCount:
      question.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value
        ? getQuestionCost(activeQuestion)
        : 0,
  };
};

const getImageDimensions = async (imageData, imageType) => {
  const blob = new Blob([imageData], { type: `image/${imageType}` });
  const url = URL.createObjectURL(blob);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ height: img.naturalHeight, width: img.naturalWidth });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    img.src = url;
  });
};

const normalizeImageType = (contentType) => {
  if (!contentType) return null;

  const match = contentType.toLowerCase().match(/image\/(jpeg|jpg|png|gif|bmp)/);
  return match ? match[1] : null;
};

const prefetchImages = async (items) => {
  const filteredItems = (items || []).filter(hasImageUrl);
  // Batch size of 6 respects HTTP/1.1 browser limit of ~6 concurrent connections per host
  const BATCH_SIZE = 6;
  const allResults = [];

  for (let i = 0; i < filteredItems.length; i += BATCH_SIZE) {
    const batch = filteredItems.slice(i, i + BATCH_SIZE);
    const batchResults = await Promise.all(
      batch.map(async (item) => {
        const imageUrl = getImageUrl(item.image);

        try {
          const response = await fetch(imageUrl);
          if (!response.ok) return null;

          const contentType = response.headers.get('content-type');
          const imageType = normalizeImageType(contentType);

          // Skip if not a supported image type (docx library only supports: jpeg, jpg, bmp, gif, png)
          if (!imageType) {
            console.warn('Unsupported image type, skipping:', contentType || 'unknown', imageUrl);
            return null;
          }

          const arrayBuffer = await response.arrayBuffer();
          if (arrayBuffer.byteLength === 0) return null;

          try {
            const dimensions = await getImageDimensions(arrayBuffer, imageType);
            if (dimensions.width <= 0 || dimensions.height <= 0) {
              console.warn('Invalid image dimensions, skipping image:', dimensions);
              return null;
            }
            const aspectRatio = dimensions.height / dimensions.width;
            const scaledHeight = MAX_IMAGE_WIDTH * aspectRatio;
            return {
              data: arrayBuffer,
              height: scaledHeight,
              id: item.id,
              type: imageType,
              width: MAX_IMAGE_WIDTH,
            };
          } catch (error) {
            console.warn('Failed to extract image dimensions, skipping image:', error);
            return null;
          }
        } catch (error) {
          console.error('Failed to fetch image:', imageUrl, error);
          return null;
        }
      })
    );
    allResults.push(...batchResults);
  }

  return allResults.reduce((map, result) => {
    if (result) {
      map[result.id] = result;
    }
    return map;
  }, {});
};

/**
 * Creates a DOCX document from survey data with formatted questions, answers, and images
 * @param {Object} survey - Survey object containing questions, groups, and title
 * @param {string} userEmail - Email address of the user creating the document
 * @returns {Promise<Document>} Promise resolving to a docx Document object
 */
export const createDoc = async (survey, userEmail) => {
  const title = survey.title || 'Untitled Survey';
  const date = new Date().toLocaleDateString();
  const createdByText = userEmail
    ? `Verified Voices Questionnaire (ID: ${survey.id}) created by ${userEmail} on ${date}`
    : `Verified Voices Questionnaire (ID: ${survey.id}) created on ${date}`;

  const response = await fetch(NUMERATOR_FULL_LOGO_URL);
  if (!response.ok) {
    const msg = 'Failed to fetch logo image';
    console.error(msg, {
      status: response.status,
      statusText: response.statusText,
      url: NUMERATOR_FULL_LOGO_URL,
    });
    throw new Error(msg);
  }
  const logo = await response.arrayBuffer();

  // Exclude soft-deleted questions and nested content from the export
  const activeQuestions = getActiveDocxQuestions(survey.questions);

  // Fetch all images in parallel (questions, nested questions, answers, and concepts)
  const { answerItems, conceptItems, questionItems } = getDocxImageFetchItems(activeQuestions);
  const [questionImages, answerImages, conceptImages] = await Promise.all([
    prefetchImages(questionItems),
    prefetchImages(answerItems),
    prefetchImages(conceptItems),
  ]);

  const questionLogic = survey.questionLogic || {};
  const questions = [];
  const questionDisplayIndexById = getQuestionDisplayIndexById(activeQuestions);

  const createImage = (imageData, imageType, width, height, indent = INDENT_LG) =>
    new Paragraph({
      children: [
        new ImageRun({
          data: imageData,
          transformation: {
            height,
            width,
          },
          type: imageType,
        }),
      ],
      indent: { left: indent },
      spacing: DEFAULT_SPACING,
    });

  const getQuestionImageParagraph = (
    questionId,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    const image = questionImages[questionId];
    return image
      ? [
          createImage(
            image.data,
            image.type,
            image.width,
            image.height,
            renderContext.questionImageIndent
          ),
        ]
      : [];
  };

  const createQuestionTitle = (question, options = {}) => {
    const displayNumber = Object.prototype.hasOwnProperty.call(options, 'displayNumber')
      ? options.displayNumber
      : (question.docxDisplayNumber ?? questionDisplayIndexById[question.id]);
    const richText = Object.prototype.hasOwnProperty.call(options, 'richText')
      ? options.richText
      : question.richText;
    const text = Object.prototype.hasOwnProperty.call(options, 'text')
      ? options.text
      : question.text || '';
    const textConfig = options.textConfig ?? BOLD_TEXT_CONFIG;
    const indent = options.indent ?? convertInchesToTwip(0.25);
    const spacing = options.spacing ?? QUESTION_SPACING;

    return new Paragraph({
      children: [
        ...(displayNumber
          ? [
              new TextRun({
                ...textConfig,
                text: `${displayNumber}. `,
              }),
            ]
          : []),
        ...(richText
          ? parseRichTextToRuns(richText, textConfig)
          : [new TextRun({ ...textConfig, text })]),
      ],
      indent: { left: indent },
      spacing,
    });
  };

  const createSectionHeader = (text, renderContext = DEFAULT_QUESTION_RENDER_CONTEXT) =>
    new Paragraph({
      children: [new TextRun({ ...BASE_TEXT_CONFIG, size: SMALL_TEXT_SIZE, text })],
      indent: { left: renderContext.sectionHeaderIndent },
      spacing: HEADER_SPACING,
    });

  const getQuestionPropertiesText = (question) => {
    const properties = question?.properties;
    const propertyTexts = [];

    if (question?.showConceptImage) {
      propertyTexts.push('Concept Image Shown');
    }

    if (properties?.maxAnswers) {
      propertyTexts.push(`Max Answers: ${properties.maxAnswers}`);
    }

    if (properties?.randomizeOptions) {
      propertyTexts.push('Randomized Order');
    }

    if (properties?.randomizeSubQuestions) {
      propertyTexts.push('Randomized Question Rows');
    }

    if (properties?.minLength) {
      propertyTexts.push(`Min Response Length: ${properties.minLength}`);
    }

    if (properties?.maxLength) {
      propertyTexts.push(`Max Response Length: ${properties.maxLength}`);
    }

    return propertyTexts.length > 0 ? ' ' + propertyTexts.map((text) => `(${text})`).join(' ') : '';
  };

  const createQuestionMetadata = (
    typeName,
    question,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT,
    extraPropertiesText = ''
  ) => {
    const propertiesText = getQuestionPropertiesText(question);
    return new Paragraph({
      children: [
        new TextRun({
          ...renderContext.metadataTextConfig,
          text: `(${typeName})${extraPropertiesText}${propertiesText}`,
        }),
      ],
      indent: { left: renderContext.metadataIndent },
      spacing: DEFAULT_SPACING,
    });
  };

  const getAnswerAttributesText = (answer) => {
    const attributes = [];

    if (answer.isAnchored) {
      attributes.push('[PINNED]');
    }

    if (answer.isExclusive) {
      attributes.push('[EXCLUSIVE]');
    }

    return attributes.length > 0 ? ' ' + attributes.join(' ') : '';
  };

  const createAnswerParagraph = (
    symbol,
    answer,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    const attributesText = getAnswerAttributesText(answer);
    const textConfig = renderContext.answerTextConfig;
    return new Paragraph({
      children: [
        new TextRun({
          ...textConfig,
          text: `${symbol}${EM_SPACE}`,
        }),
        ...(answer.richText
          ? parseRichTextToRuns(answer.richText, textConfig)
          : [new TextRun({ ...textConfig, text: answer.text || '' })]),
        new TextRun({
          ...textConfig,
          text: attributesText,
        }),
      ],
      indent: { left: renderContext.answerIndent },
      spacing: { after: convertInchesToTwip(0.05) },
    });
  };

  const createAnswerParagraphs = (
    answers,
    symbol,
    imagesMap = {},
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) =>
    (answers || []).flatMap((answer) => {
      const paragraphs = [createAnswerParagraph(symbol, answer, renderContext)];
      const image = imagesMap[answer.id];
      if (image) {
        paragraphs.push(
          createImage(
            image.data,
            image.type,
            image.width,
            image.height,
            renderContext.answerImageIndent
          )
        );
      }
      return paragraphs;
    });

  // Helper to create plain descriptive text
  const createDescriptiveText = (
    text,
    {
      indent = DEFAULT_QUESTION_RENDER_CONTEXT.descriptiveIndent,
      size = DEFAULT_QUESTION_RENDER_CONTEXT.descriptiveTextSize,
    } = {}
  ) =>
    new Paragraph({
      children: [
        new TextRun({
          ...BASE_TEXT_CONFIG,
          italics: true,
          size,
          text,
        }),
      ],
      indent: { left: indent },
      spacing: DEFAULT_SPACING,
    });

  const getDescriptiveTextOptions = (renderContext, indent = renderContext.descriptiveIndent) => ({
    indent,
    size: renderContext.descriptiveTextSize,
  });

  const getDisqualifyParagraphs = (
    disqualifyLogic,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    if (!disqualifyLogic?.answers?.length) return [];

    const paragraphs = [
      createDescriptiveText(
        `Disqualify if the answer is [${disqualifyLogic.operator?.text || ''}]:`,
        getDescriptiveTextOptions(renderContext)
      ),
    ];

    disqualifyLogic.answers.forEach((ans) =>
      paragraphs.push(
        createDescriptiveText(
          `${EM_SPACE}${BULLET_SYMBOL} ${ans.text}`,
          getDescriptiveTextOptions(renderContext)
        )
      )
    );

    return paragraphs;
  };

  const getMultiLevelDisqualifyParagraphs = (
    disqualifyLogic,
    groups,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    const conditions = disqualifyLogic?.logic || [];
    if (conditions.length === 0) return [];

    const disqualifyOperator = disqualifyLogic.operator?.text || 'All';
    const paragraphs = [
      createDescriptiveText(
        `Disqualify if [${disqualifyOperator}] apply:`,
        getDescriptiveTextOptions(renderContext)
      ),
    ];

    conditions.forEach((condition) => {
      const operator = condition.operator?.text || '';
      if (condition.answers?.length) {
        paragraphs.push(
          createDescriptiveText(
            `${EM_SPACE}${BULLET_SYMBOL} The answer is [${operator}]:`,
            getDescriptiveTextOptions(renderContext)
          )
        );
        condition.answers.forEach((answer) =>
          paragraphs.push(
            createDescriptiveText(
              `${DASH_SYMBOL} ${answer.text}`,
              getDescriptiveTextOptions(renderContext, renderContext.descriptiveChildIndent)
            )
          )
        );
      } else if (condition.url_variable || condition.values) {
        const groupNames = condition.values?.length
          ? condition.values
              .map((id) => groups?.find((group) => Number(group.id) === Number(id))?.name)
              .filter(Boolean)
              .join(', ')
          : '';
        paragraphs.push(
          createDescriptiveText(
            `${EM_SPACE}${BULLET_SYMBOL} The quota group is [${operator}]: ${groupNames}`,
            getDescriptiveTextOptions(renderContext)
          )
        );
      }
    });

    return paragraphs;
  };

  const getSkipLogicParagraphs = (
    skipLogic,
    questionType,
    groups,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    const conditions = skipLogic?.logic || [];
    if (conditions.length === 0) return [];

    const skipOperator = skipLogic.operator?.text || 'All';
    const isInstructional = questionType === QUESTION_TYPES.INSTRUCTIONS.value;
    const skipText = isInstructional ? 'Show this text' : 'Ask this question';

    const paragraphs = [
      createDescriptiveText(
        `${skipText} if [${skipOperator}] apply:`,
        getDescriptiveTextOptions(renderContext)
      ),
    ];

    conditions.forEach((condition) => {
      if (condition.question) {
        // Question-based skip logic
        const questionNum = questionDisplayIndexById[condition.question?.id] || '?';
        const operator = condition.operator?.text || '';

        // Regular answer text (bulleted options list)
        if (condition.answers?.length) {
          const answerTexts = condition.answers.map((ans) => `${DASH_SYMBOL} ${ans.text}`);
          paragraphs.push(
            createDescriptiveText(
              `${EM_SPACE}${BULLET_SYMBOL} The answer to (Q${questionNum}) is [${operator}]:`,
              getDescriptiveTextOptions(renderContext)
            )
          );
          answerTexts.forEach((answerText) =>
            paragraphs.push(
              createDescriptiveText(
                `${answerText}`,
                getDescriptiveTextOptions(renderContext, renderContext.descriptiveChildIndent)
              )
            )
          );
          // Numeric comparison
        } else {
          paragraphs.push(
            createDescriptiveText(
              `${EM_SPACE}${BULLET_SYMBOL} The answer to (Q${questionNum}) is [${operator}]: ${condition.comparison_value || ''}`,
              getDescriptiveTextOptions(renderContext)
            )
          );
        }
      } else if (condition.url_variable || condition.values) {
        // Quota group skip logic
        const operator = condition.operator?.text || '';
        const groupNames = condition.values?.length
          ? condition.values
              .map((id) => groups?.find((g) => Number(g.id) === Number(id))?.name)
              .filter(Boolean)
              .join(', ')
          : '';
        paragraphs.push(
          createDescriptiveText(
            `${EM_SPACE}${BULLET_SYMBOL} The quota group is [${operator}]: ${groupNames}`,
            getDescriptiveTextOptions(renderContext)
          )
        );
      }
    });

    return paragraphs;
  };

  const getQuestionLogicParagraphs = (
    question,
    groups,
    renderContext = DEFAULT_QUESTION_RENDER_CONTEXT
  ) => {
    const logic = questionLogic[question.id];
    if (!logic) return [];

    const logicParagraphs = [
      ...(Array.isArray(logic.disqualify?.logic)
        ? getMultiLevelDisqualifyParagraphs(logic.disqualify, groups, renderContext)
        : getDisqualifyParagraphs(logic.disqualify, renderContext)),
      ...getSkipLogicParagraphs(logic.skip, question.type, groups, renderContext),
    ];

    if (logicParagraphs.length === 0) return [];

    return [createSectionHeader('[LOGIC]', renderContext), ...logicParagraphs];
  };

  const createGridTableCell = (
    content,
    textConfig = BASE_TEXT_CONFIG,
    align = VerticalAlign.TOP
  ) => {
    const text = typeof content === 'string' ? content : content.text;
    const image = typeof content === 'string' ? null : content.image;
    const children = [
      new Paragraph({
        children: [new TextRun({ ...textConfig, text })],
        spacing: DEFAULT_SPACING,
      }),
    ];

    if (image) {
      children.push(createImage(image.data, image.type, image.width, image.height, 0));
    }

    return new TableCell({
      children,
      verticalAlign: align,
      width: { size: 50, type: WidthType.PERCENTAGE },
    });
  };

  const createGridTable = (question, renderContext = DEFAULT_QUESTION_RENDER_CONTEXT) => {
    const subQuestions = getActiveDocxItems(question.subQuestions);
    const answers = getActiveDocxItems(question.answers);
    const maxRows = Math.max(subQuestions.length, answers.length);

    const rows = [
      // Header row
      new TableRow({
        children: [
          createGridTableCell(
            'Grid Rows',
            renderContext.gridHeaderTextConfig,
            VerticalAlign.CENTER
          ),
          createGridTableCell(
            'Grid Columns',
            renderContext.gridHeaderTextConfig,
            VerticalAlign.CENTER
          ),
        ],
      }),
      // Content rows
      ...Array.from({ length: maxRows }, (_, idx) => {
        const subQuestion = subQuestions[idx];
        const answer = answers[idx];
        const rowText = subQuestion ? `${idx + 1}. ${subQuestion.text}` : '';
        const colText = answer ? `${String.fromCharCode(A_CHAR_CODE + idx)}. ${answer.text}` : '';
        const rowImage = subQuestion ? questionImages[subQuestion.id] : null;
        const colImage = answer ? answerImages[answer.id] : null;

        return new TableRow({
          children: [
            createGridTableCell(
              { image: rowImage, text: rowText },
              renderContext.gridBodyTextConfig
            ),
            createGridTableCell(
              { image: colImage, text: colText },
              renderContext.gridBodyTextConfig
            ),
          ],
        });
      }),
    ];

    return new Table({
      borders: BORDERLESS_TABLE_CONFIG.borders,
      indent: { size: renderContext.gridIndent, type: WidthType.DXA },
      rows,
      width: { size: renderContext.gridWidth, type: WidthType.PERCENTAGE },
    });
  };

  const createGridQuestion = (question, renderContext = DEFAULT_QUESTION_RENDER_CONTEXT) => {
    const questionCost = getQuestionCost(question);

    return [
      createQuestionTitle(question, renderContext.titleOptions),
      createQuestionMetadata(QUESTION_TYPES.TABLE.name, question, renderContext),
      ...getQuestionImageParagraph(question.id, renderContext),
      createDescriptiveText(
        `This will count as ${questionCost} question${questionCost !== 1 ? 's' : ''} for your survey.`,
        getDescriptiveTextOptions(renderContext)
      ),
      createGridTable(question, renderContext),
      ...getQuestionLogicParagraphs(question, survey.groups, renderContext),
    ];
  };

  const createStandardQuestion = (
    question,
    {
      answers = question.answers,
      descriptiveText = null,
      metadataText = '',
      renderContext = DEFAULT_QUESTION_RENDER_CONTEXT,
      symbol = null,
      typeName,
    }
  ) => [
    createQuestionTitle(question, renderContext.titleOptions),
    createQuestionMetadata(typeName, question, renderContext, metadataText),
    ...getQuestionImageParagraph(question.id, renderContext),
    ...(descriptiveText
      ? [createDescriptiveText(descriptiveText, getDescriptiveTextOptions(renderContext))]
      : []),
    ...(symbol ? createAnswerParagraphs(answers, symbol, answerImages, renderContext) : []),
    ...getQuestionLogicParagraphs(question, survey.groups, renderContext),
  ];

  const createFallbackQuestion = (question, renderContext = DEFAULT_QUESTION_RENDER_CONTEXT) => [
    createQuestionTitle(question, renderContext.titleOptions),
    ...getQuestionImageParagraph(question.id, renderContext),
    ...getQuestionLogicParagraphs(question, survey.groups, renderContext),
  ];

  const createConceptParagraphs = (question, concepts) =>
    concepts.flatMap((concept, conceptIndex) => {
      const conceptName = concept.name || `Concept ${conceptIndex + 1}`;
      const conceptImage = conceptImages[getConceptImageKey(question, concept, conceptIndex)];
      const paragraphs = [
        new Paragraph({
          children: [
            new TextRun({
              ...BASE_TEXT_CONFIG,
              text: conceptName,
            }),
          ],
          indent: { left: INDENT_LG },
          spacing: DEFAULT_SPACING,
        }),
      ];

      if (conceptImage) {
        paragraphs.push(
          createImage(
            conceptImage.data,
            conceptImage.type,
            conceptImage.width,
            conceptImage.height,
            INDENT_LG
          )
        );
      }

      return paragraphs;
    });

  const getSequentialMonadicDescription = (question) => {
    const { conceptCount, questionCount } = getSequentialMonadicSectionDetails(question);
    const randomizationLabel = question.properties?.randomizeSubQuestions ? 'Randomized. ' : '';

    return (
      `${randomizationLabel}Panelists will see all of these questions ${conceptCount} time` +
      `${conceptCount === 1 ? '' : 's'} based on the following concepts. ` +
      `This section counts as ${questionCount} question${questionCount === 1 ? '' : 's'}.`
    );
  };

  const createSequentialMonadicQuestion = (question) => {
    const { activeConcepts, activeSubQuestions } = getSequentialMonadicSectionDetails(question);

    return [
      createQuestionTitle(question, {
        displayNumber: null,
        richText: null,
        text: MONADIC_SECTION_TITLE,
      }),
      createQuestionMetadata(QUESTION_TYPES.SEQUENTIAL_MONADIC.name, question),
      ...getQuestionImageParagraph(question.id),
      createDescriptiveText(getSequentialMonadicDescription(question)),
      createSectionHeader('[CONCEPTS]'),
      ...createConceptParagraphs(question, activeConcepts),
      createSectionHeader('[QUESTIONS]'),
      ...activeSubQuestions.flatMap((subQuestion, index) =>
        createQuestionParagraphs(
          {
            ...subQuestion,
            docxDisplayNumber: String(index + 1),
          },
          { renderContext: NESTED_QUESTION_RENDER_CONTEXT }
        )
      ),
      ...getQuestionLogicParagraphs(question, survey.groups),
    ];
  };

  const createQuestionParagraphs = (question, options = {}) => {
    const { renderContext = DEFAULT_QUESTION_RENDER_CONTEXT } = options;

    switch (question.type) {
      case QUESTION_TYPES.CHECKBOX.value:
        return createStandardQuestion(question, {
          renderContext,
          symbol: CHECKBOX_SYMBOL,
          typeName: QUESTION_TYPES.CHECKBOX.name,
        });
      case QUESTION_TYPES.CONT_SUM.value:
      case QUESTION_TYPES.CONTSUM.value:
        return createStandardQuestion(question, {
          renderContext,
          symbol: FILLED_CIRCLE_SYMBOL,
          typeName: QUESTION_TYPES.CONTSUM.name,
        });
      case QUESTION_TYPES.ESSAY.value:
        return createStandardQuestion(question, {
          descriptiveText: "Panelist's write-in",
          renderContext,
          typeName: QUESTION_TYPES.ESSAY.name,
        });
      case QUESTION_TYPES.INSTRUCTIONS.value:
        return createStandardQuestion(question, {
          descriptiveText: 'This instructional text does not count as a question',
          renderContext,
          typeName: QUESTION_TYPES.INSTRUCTIONS.name,
        });
      case QUESTION_TYPES.MAX_DIFF.value: {
        const maxDiffInfo = getMaxDiffQuestionInfo(question);
        return createStandardQuestion(question, {
          descriptiveText: maxDiffInfo.getText(),
          renderContext,
          symbol: FILLED_CIRCLE_SYMBOL,
          typeName: QUESTION_TYPES.MAX_DIFF.name,
        });
      }
      case QUESTION_TYPES.NUMERIC.value:
        return createStandardQuestion(question, {
          descriptiveText: `Panelist's ${getNumericTypeText(question.properties)}`,
          renderContext,
          typeName: QUESTION_TYPES.NUMERIC.name,
        });
      case QUESTION_TYPES.RADIO.value:
        return createStandardQuestion(question, {
          renderContext,
          symbol: RADIO_SYMBOL,
          typeName: QUESTION_TYPES.RADIO.name,
        });
      case QUESTION_TYPES.RANK.value:
      case QUESTION_TYPES['RANK-DRAGDROP'].value:
        return createStandardQuestion(question, {
          renderContext,
          symbol: FILLED_CIRCLE_SYMBOL,
          typeName: QUESTION_TYPES.RANK.name,
        });
      case QUESTION_TYPES.SEQUENTIAL_MONADIC.value:
        return createSequentialMonadicQuestion(question);
      case QUESTION_TYPES.TABLE.value:
      case QUESTION_TYPES['TABLE-RADIO'].value:
        return createGridQuestion(question, renderContext);
      case QUESTION_TYPES.TURF.value:
        return createStandardQuestion(question, {
          renderContext,
          symbol: CHECKBOX_SYMBOL,
          typeName: QUESTION_TYPES.TURF.name,
        });
      case QUESTION_TYPES.VAN_WESTENDORP.value: {
        const answers =
          question.properties?.displayOrder === VW_DISPLAY_ORDERS.HIGH_TO_LOW.value
            ? [...(question.answers || [])].reverse()
            : question.answers;
        const numericText = getNumericTypeText(question.properties);
        return createStandardQuestion(question, {
          answers,
          metadataText: ` (${numericText.charAt(0).toUpperCase()}${numericText.slice(1)})`,
          renderContext,
          symbol: FILLED_CIRCLE_SYMBOL,
          typeName: QUESTION_TYPES.VAN_WESTENDORP.name,
        });
      }
      case QUESTION_TYPES.VIDEO.value:
        return createStandardQuestion(question, {
          descriptiveText: "Panelist's video response",
          renderContext,
          typeName: QUESTION_TYPES.VIDEO.name,
        });
      default:
        return createFallbackQuestion(question, renderContext);
    }
  };

  activeQuestions.forEach((question) => {
    questions.push(...createQuestionParagraphs(question));
  });

  return new Document({
    numbering: {
      config: [BULLETED_LIST_CONFIG],
    },
    sections: [
      {
        children: [
          // HEADER
          new Paragraph({
            children: [
              new ImageRun({
                altText: {
                  description: 'Numerator Full Logo',
                  name: 'Numerator Full Logo',
                  title: 'Numerator Full Logo',
                },
                data: logo,
                transformation: {
                  height: 27,
                  width: 157,
                },
                type: 'png',
              }),
            ],
            spacing: HEADER_SPACING,
          }),
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, size: 28, text: title })],
            spacing: HEADER_SPACING,
          }),
          new Paragraph({
            children: [
              new TextRun({
                ...BASE_TEXT_CONFIG,
                text: createdByText,
              }),
            ],
            spacing: DEFAULT_SPACING,
          }),
          new Paragraph({
            children: [new TextRun({ ...BASE_TEXT_CONFIG, text: 'Powered by Verified Voices' })],
          }),
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, text: 'Response Counts:' })],
            spacing: BLOCK_SPACING,
          }),
          ...(survey.groups || []).map(
            (group) =>
              new Paragraph({
                children: [
                  new TextRun({
                    ...BOLD_TEXT_CONFIG,
                    text: `${group.name || ''}: ${group.quotaLimit || 0}`,
                  }),
                ],
                numbering: { level: 0, reference: 'bulleted-list' },
                spacing: DEFAULT_SPACING,
              })
          ),
          // QUESTIONNAIRE
          new Paragraph({
            children: [new TextRun({ ...BOLD_TEXT_CONFIG, text: 'Questionnaire:' })],
            spacing: BLOCK_SPACING,
          }),
          ...questions,
        ],
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.END,
                children: [
                  new TextRun({
                    children: [PageNumber.CURRENT],
                  }),
                ],
              }),
            ],
          }),
        },
        properties: {
          page: {
            pageNumbers: {
              formatType: NumberFormat.DECIMAL,
              start: 1,
            },
          },
        },
      },
    ],
  });
};

/**
 * Generates and downloads a DOCX file from a Document object
 * @param {Document} doc - docx Document object to convert and download
 * @param {string} fileName - Name for the downloaded file (including .docx extension)
 * @returns {Promise<void>}
 */
export const generateFile = async (doc, fileName) => {
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);

  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

export const getSurveyDocxFileName = (survey) => {
  const defaultTitle = `survey_${survey.id}`;
  const title = survey.title || defaultTitle;
  const sanitizedTitle =
    title
      .replace(/[/\\:*?"<>|]/g, '_')
      .replace(/\.+$/g, '')
      .trim() || defaultTitle;

  return `${sanitizedTitle}.docx`;
};

export const downloadDOCXFile = async ({ survey, userEmail }) => {
  const doc = await createDoc(survey, userEmail);
  await generateFile(doc, getSurveyDocxFileName(survey));
};

export const parseRichTextToRuns = (html, baseConfig = BASE_TEXT_CONFIG) => {
  const runs = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const processNode = (node, styles = {}) => {
    if (node.nodeType === Node.TEXT_NODE) {
      if (node.textContent) {
        runs.push(
          new TextRun({
            ...baseConfig,
            ...styles,
            text: node.textContent,
          })
        );
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();

    const nextStyles = {
      ...styles,
      ...(tag === 'b' || tag === 'strong' ? { bold: true } : {}),
      ...(tag === 'i' || tag === 'em' ? { italics: true } : {}),
      ...(tag === 'u' ? { underline: {} } : {}),
    };

    node.childNodes.forEach((child) => processNode(child, nextStyles));
  };

  doc.body.childNodes.forEach((node) => processNode(node));
  return runs;
};
